import { useState } from 'react';
import { Navbar, Nav, Container, NavDropdown, Offcanvas, Button, Badge } from 'react-bootstrap';
import { FaUser, FaBars, FaTimes } from 'react-icons/fa';
import { LinkContainer } from 'react-router-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '../slices/usersApiSlice';
import { logout } from '../slices/authSlice';
import SearchBox from './SearchBox';
import logo from '../assets/BeaCrest.jpg';
import { resetCart } from '../slices/cartSlice';
import { useRecordLogoutMutation } from '../slices/userLoginAuditApiSlice';

import { apiSlice } from '../slices/apiSlice';

// ── NEW: swap pending-count queries ──────────────────────────────────────────
import {
  useGetMySwapRequestsQuery,
  useGetSwapRequestsForApprovalQuery,
} from '../slices/swapApiSlice';

// ─── Styles ───────────────────────────────────────────────────────────────────
const dropdownMenuStyle = { zIndex: 1050, backgroundColor: 'lightblue' };
const dropdownItemStyle = { backgroundColor: 'lightyellow' };

// Offcanvas section header
const SectionLabel = ({ children }) => (
  <div style={{
    margin: '10px 12px 6px',
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#20303e',
    backgroundColor: '#dde3e8',
    borderRadius: 6,
  }}>
    {children}
  </div>
);

// Desktop NavDropdown section header — boxed gray label between groups of items.
const DropdownSectionHeader = ({ children }) => (
  <div style={{
    margin: '6px 14px 4px',
    padding: '7px 14px',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#20303e',
    backgroundColor: '#dde3e8',
    borderRadius: 6,
  }}>
    {children}
  </div>
);

const openGuide = async (guideKey) => {
  try {
    const res = await fetch(`/api/guides/${guideKey}`, { credentials: 'include' });
    if (!res.ok) {
      let message = 'Unable to open guide.';
      try {
        const data = await res.json();
        message = data.message || message;
      } catch (_) {}
      alert(message);
      return;
    }
    const html = await res.text();
    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  } catch (err) {
    console.error('Failed to open guide:', err);
    alert('Unable to open guide.');
  }
};

// Small red count badge — used next to nav items with pending items needing action
const CountBadge = ({ count }) => {
  if (!count) return null;
  return (
    <Badge
      bg="danger"
      style={{ marginLeft: 8, fontSize: '0.65rem', verticalAlign: 'middle' }}
    >
      {count}
    </Badge>
  );
};

// Single flat nav item for the offcanvas drawer
const DrawerItem = ({ to, href, onClick, children }) => {
  const style = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '13px 20px',
    fontSize: 16,
    color: '#1a1a2e',
    textDecoration: 'none',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#fff',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    border: 'none',
  };

  if (href) {
    return ( <a

        href={href}
        target='_blank'
        rel='noopener noreferrer'
        style={style}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  if (to) {
    return (
      <LinkContainer to={to}>
        <div style={style} onClick={onClick}>{children}</div>
      </LinkContainer>
    );
  }
  return (
    <button style={style} onClick={() => { onClick?.(); }}>
      {children}
    </button>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const Header = () => {
  const [showDrawer, setShowDrawer] = useState(false);

  const { userInfo } = useSelector((state) => state.auth);
  const dispatch     = useDispatch();
  const navigate     = useNavigate();

  const [logoutApiCall]  = useLogoutMutation();
  const [recordLogout]   = useRecordLogoutMutation();

  const closeDrawer = () => setShowDrawer(false);

  // ── NEW: pending swap counts ────────────────────────────────────────────────
  // Staff: shifts offered to them, still open (PendingAcceptance).
  // Reuses the same query MySwapRequestsScreen already uses, so this shares
  // its cache entry rather than firing a duplicate network request.
  const { data: myOfferedSwaps } = useGetMySwapRequestsQuery(undefined, {
    skip: !userInfo,
    pollingInterval: 60000,
  });
  const pendingSwapCount = myOfferedSwaps?.length || 0;

  // Manager/Admin: swaps awaiting approval, scoped by the backend exactly as
  // SwapApprovalScreen sees them (admin = hospital-wide, manager = own
  // directorate + ward inclusions).
  const { data: pendingApprovals } = useGetSwapRequestsForApprovalQuery(undefined, {
    skip: !(userInfo?.isManager || userInfo?.isAdmin),
    pollingInterval: 60000,
  });
  const pendingApprovalCount = pendingApprovals?.length || 0;

  const logoutHandler = async () => {
    try {
      await recordLogout({ eventType: 'logout_manual' }).unwrap();
      dispatch(logout());
      dispatch(resetCart());
      dispatch(apiSlice.util.resetApiState());   // ← clears cached data so the next
                                                  //   login on this browser starts fresh
      closeDrawer();
      navigate('/login');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <header>
      {/* ── Desktop navbar (lg and above) ── */}
      <Navbar
        style={{ backgroundColor: '#b1e1e7', height: '80px' }}
        variant='light'
        expand='lg'
        collapseOnSelect
        className='d-none d-lg-flex'
      >
        <Container>
          <LinkContainer to='/'>
            <Navbar.Brand>
              <img
                src={logo}
                alt='Beaumont Hospital Agency Portal'
                style={{ maxHeight: '60px', maxWidth: '100%', height: 'auto' }}
              />
              <Navbar.Text style={{ fontSize: '30px', marginLeft: '30px' }}>
                Beaumont Hospital Shift Availability Portal
              </Navbar.Text>
            </Navbar.Brand>
          </LinkContainer>

          <Navbar.Collapse id='basic-navbar-nav'>
            <Nav className='ms-auto'>
              <SearchBox />

              {/* User dropdown   removed link  <LinkContainer to='/my-availability'>
                    <NavDropdown.Item style={dropdownItemStyle}>My Availability</NavDropdown.Item>
                  </LinkContainer>*/}


              {userInfo ? (
                <NavDropdown
                  title={
                    <>
                      {userInfo.name}
                      <CountBadge count={pendingSwapCount} />
                    </>
                  }
                  id='username'
                  style={dropdownMenuStyle}
                >
                  <LinkContainer to='/profile'>
                    <NavDropdown.Item style={dropdownItemStyle}>Profile.....</NavDropdown.Item>
                  </LinkContainer>
                  
                  <LinkContainer to='/my-roster'>
                    <NavDropdown.Item style={dropdownItemStyle}>My Roster</NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/my-shifts/request-swap'>
                    <NavDropdown.Item style={dropdownItemStyle}>
                      My Shift Swaps
                      <CountBadge count={pendingSwapCount} />
                    </NavDropdown.Item>
                  </LinkContainer>
{/*
                  {!userInfo?.isManager && !userInfo?.isAdmin && (
                    <LinkContainer to='/arcade'>
                      <NavDropdown.Item style={dropdownItemStyle}>My Skills</NavDropdown.Item>
                    </LinkContainer>
                  )}


*/}

                    <NavDropdown.Divider />
                      <NavDropdown.Item
                        onClick={() => openGuide('staff')}
                        style={dropdownItemStyle}
                      >
                        Staff Guide
                      </NavDropdown.Item>


                  <NavDropdown.Item onClick={logoutHandler}>Logout</NavDropdown.Item>
                </NavDropdown>
              ) : (
                <LinkContainer to='/login'>
                  <Nav.Link><FaUser /> Sign In</Nav.Link>
                </LinkContainer>
              )}

              {/* Manager dropdown */}
              {userInfo?.isManager && (
                <NavDropdown
                  title={
                    <>
                      Manager
                      <CountBadge count={pendingApprovalCount} />
                    </>
                  }
                  id='managermenu'
                  style={dropdownMenuStyle}
                >
                  <LinkContainer to='/manager/ward-preshifts'>
                    <NavDropdown.Item style={dropdownItemStyle}>Request-Shifts</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/manager/ward-weekly-schedule'>
                    <NavDropdown.Item style={dropdownItemStyle}>Ward Weekly Schedule</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/manager/approved-filled'>
                    <NavDropdown.Item style={dropdownItemStyle}>Worked Shifts (Pending) Approval</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/manager/completed-shifts'>
                    <NavDropdown.Item style={dropdownItemStyle}>Approved and Completed</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/manager/MonthlyCalendarView'>
                    <NavDropdown.Item style={dropdownItemStyle}>Monthly Calendar Stats</NavDropdown.Item>
                  </LinkContainer>
                  <NavDropdown.Divider />
                  <LinkContainer to='/manager/staffWeeklySchedule'>
                    <NavDropdown.Item style={dropdownItemStyle}>Staff Weekly Schedule</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/manager/staff-availability'>
                    <NavDropdown.Item style={dropdownItemStyle}>Staff Availability</NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/manager/swap-approvals'>
                    <NavDropdown.Item style={dropdownItemStyle}>
                      Shift Swap Approvals
                      <CountBadge count={pendingApprovalCount} />
                    </NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/manager/dashboard'>
                    <NavDropdown.Item style={dropdownItemStyle}>Directorate Dashboard</NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/manager/agency-dashboard'>
                    <NavDropdown.Item style={dropdownItemStyle}>Agency Dashboard</NavDropdown.Item>
                  </LinkContainer>

                  <NavDropdown.Divider />
                    <NavDropdown.Item
                      onClick={() => openGuide('manager')}
                      style={dropdownItemStyle}
                    >
                      Manager Guide
                    </NavDropdown.Item>

                    <NavDropdown.Item
                        onClick={() => openGuide('early-finishes')}
                        style={dropdownItemStyle}
                      >
                        Shift - Early Finishes
                      </NavDropdown.Item>


                </NavDropdown>
              )}

              {/* Admin dropdown */}
              {userInfo?.isAdmin && (
                <NavDropdown
                  title={
                    <>
                      Admin
                      <CountBadge count={pendingApprovalCount} />
                    </>
                  }
                  id='adminmenu'
                  style={dropdownMenuStyle}
                >
                  <DropdownSectionHeader>Shift Assignments</DropdownSectionHeader>

                  <LinkContainer to='/admin/shifts'>
                    <NavDropdown.Item style={dropdownItemStyle}>Create and Manage Live Shifts</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/staff-availability'>
                    <NavDropdown.Item style={dropdownItemStyle}>Staff Availability</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/ward-weekly-schedule'>
                    <NavDropdown.Item style={dropdownItemStyle}>Ward Weekly Schedule</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/staffWeeklySchedule'>
                    <NavDropdown.Item style={dropdownItemStyle}>Staff Weekly Schedule</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/ward-preshifts'>
                    <NavDropdown.Item style={dropdownItemStyle}>Manager Pre-Shift Requests</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/rolling-roster'>
                    <NavDropdown.Item style={dropdownItemStyle}>6-Week Rolling Roster</NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/manager/swap-approvals'>
                    <NavDropdown.Item style={dropdownItemStyle}>
                      Shift Swap Approvals
                      <CountBadge count={pendingApprovalCount} />
                    </NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/admin/approved-filled'>
                    <NavDropdown.Item style={dropdownItemStyle}>Worked Shifts (Pending) Approval</NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/admin/completed-shifts'>
                    <NavDropdown.Item style={dropdownItemStyle}>Approved and Completed</NavDropdown.Item>
                  </LinkContainer>

                  <DropdownSectionHeader>Dashboards</DropdownSectionHeader>

                  <LinkContainer to='/admin/dashboard'>
                    <NavDropdown.Item style={dropdownItemStyle}>Directorate Dashboard</NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/admin/agency-dashboard'>
                    <NavDropdown.Item style={dropdownItemStyle}>Agency Dashboard</NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/admin/preshift-analytics'>
                    <NavDropdown.Item style={dropdownItemStyle}>Pre Shift Requests Dashboard</NavDropdown.Item>
                  </LinkContainer>

                  <LinkContainer to='/admin/MonthlyCalendarView'>
                    <NavDropdown.Item style={dropdownItemStyle}>Monthly Calendar Stats</NavDropdown.Item>
                  </LinkContainer>

                  <DropdownSectionHeader>Agency</DropdownSectionHeader>


                  <LinkContainer to='/admin/agencies'>
                    <NavDropdown.Item style={dropdownItemStyle}>Manage Agencies</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/agency-tier-response-times'>
                    <NavDropdown.Item style={dropdownItemStyle}>Agency Tier Response Times</NavDropdown.Item>
                  </LinkContainer>


                  

                  <LinkContainer to='/admin/agency-shifts'>
                    <NavDropdown.Item style={dropdownItemStyle}>Agency Email, Shifts Requests</NavDropdown.Item>
                  </LinkContainer>

                  {/* External site — the agency-facing portal, a separate app on its
                      own subdomain, so a plain anchor (not LinkContainer) opened in
                      a new tab rather than an internal route. */}
                  <NavDropdown.Item
                    as="a"
                    href="https://agencyinterface.beaumont-portals.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={dropdownItemStyle}
                  >
                    Agency Interface
                  </NavDropdown.Item>

                  

                  <DropdownSectionHeader>Configuration</DropdownSectionHeader>

                  <LinkContainer to='/admin/userlist'>
                    <NavDropdown.Item style={dropdownItemStyle}>Users</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/hospital-locations'>
                    <NavDropdown.Item style={dropdownItemStyle}>Manage Hospital Locations</NavDropdown.Item>
                  </LinkContainer>
                  {/* ── NEW: Availability Type Configuration ── */}
                  <LinkContainer to='/admin/availability-config'>
                    <NavDropdown.Item style={dropdownItemStyle}>Availability Type Configuration</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/audit-log'>
                    <NavDropdown.Item style={dropdownItemStyle}>Shift Audit Log</NavDropdown.Item>
                  </LinkContainer>
                  {/* ── NEW ── */}
                  <NavDropdown.Divider />
                  <DropdownSectionHeader>Annual Leave</DropdownSectionHeader>
                  <LinkContainer to='/admin/annual-leave'>
                    <NavDropdown.Item style={dropdownItemStyle}>Annual Leave Calculator</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/non-bank-annual-leave'>
                    <NavDropdown.Item style={dropdownItemStyle}>Non Bank Annual Leave</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/annual-leave-config'>
                    <NavDropdown.Item style={dropdownItemStyle}>Annual Leave Configuration</NavDropdown.Item>
                  </LinkContainer>

                  <NavDropdown.Divider />


                  <DropdownSectionHeader>Guides</DropdownSectionHeader>

                  <NavDropdown.Item
                        onClick={() => openGuide('admin')}
                        style={dropdownItemStyle}
                      >
                        Admin Guide
                      </NavDropdown.Item>

                      <NavDropdown.Item
                        onClick={() => openGuide('manager')}
                        style={dropdownItemStyle}
                      >
                        Manager Guide
                      </NavDropdown.Item>


                      <NavDropdown.Item
                          onClick={() => openGuide('early-finishes')}
                          style={dropdownItemStyle}
                        >
                          Shift - Early Finishes
                        </NavDropdown.Item>

                        <NavDropdown.Item
                          onClick={() => openGuide('agency-portal-guide')}
                          style={dropdownItemStyle}
                        >
                          Agency portal guide
                        </NavDropdown.Item>


                        <NavDropdown.Item
                            onClick={() => openGuide('annual-leave')}
                            style={dropdownItemStyle}
                          >
                            Annual Leave Entitlement Guide
                          </NavDropdown.Item>

                          <NavDropdown.Item
                    onClick={() => openGuide('rolling-roster')}
                    style={dropdownItemStyle}
                  >
                    6-Week Rolling Roster Guide
                  </NavDropdown.Item>







                </NavDropdown>
              )}

              {/* Reporter dropdown — read-only dashboard/report access, no admin privileges */}
                {userInfo?.isReporter && !userInfo?.isAdmin && (
                  <NavDropdown title='Reports' id='reportermenu' style={dropdownMenuStyle}>
                    <LinkContainer to='/admin/dashboard'>
                      <NavDropdown.Item style={dropdownItemStyle}>Directorate Dashboard</NavDropdown.Item>
                    </LinkContainer>
                    <LinkContainer to='/admin/completed-shifts'>
                      <NavDropdown.Item style={dropdownItemStyle}>Approved and Completed</NavDropdown.Item>
                    </LinkContainer>
                    <LinkContainer to='/admin/agency-dashboard'>
                      <NavDropdown.Item style={dropdownItemStyle}>Agency Dashboard</NavDropdown.Item>
                    </LinkContainer>
                    <LinkContainer to='/admin/preshift-analytics'>
                      <NavDropdown.Item style={dropdownItemStyle}>Pre Shift Requests Dashboard</NavDropdown.Item>
                    </LinkContainer>

                    <LinkContainer to='/admin/audit-log'>
                      <NavDropdown.Item style={dropdownItemStyle}>Shift Audit Log</NavDropdown.Item>
                    </LinkContainer>

                    <NavDropdown.Divider />
                    <LinkContainer to='/admin/annual-leave'>
                      <NavDropdown.Item style={dropdownItemStyle}>Annual Leave Calculator</NavDropdown.Item>
                    </LinkContainer>
                    <LinkContainer to='/admin/non-bank-annual-leave'>
                      <NavDropdown.Item style={dropdownItemStyle}>Non Bank Annual Leave</NavDropdown.Item>
                    </LinkContainer>
                    <LinkContainer to='/admin/annual-leave-config'>
                      <NavDropdown.Item style={dropdownItemStyle}>Annual Leave Configuration</NavDropdown.Item>
                    </LinkContainer>

                  </NavDropdown>
                )}


              {/* Rad Admin dropdown */}
              {userInfo?.isRadAdmin && (
                <NavDropdown title='Radiation Admin' id='radadminmenu' style={dropdownMenuStyle}>
                  <LinkContainer to='/admin/shiftApproval'>
                    <NavDropdown.Item style={dropdownItemStyle}>Shift Approval</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/shiftAgencyNotification'>
                    <NavDropdown.Item style={dropdownItemStyle}>Agency Notification</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/shiftRequestAgency'>
                    <NavDropdown.Item style={dropdownItemStyle}>Agency Request</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/shiftRequests'>
                    <NavDropdown.Item style={dropdownItemStyle}>Staff Shift Requests</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/RadAdmin/radiationsafetylist'>
                    <NavDropdown.Item style={dropdownItemStyle}>Radiation Safety Records</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/Admin/userlist/Rad'>
                    <NavDropdown.Item style={dropdownItemStyle}>Users</NavDropdown.Item>
                  </LinkContainer>
                </NavDropdown>
              )}

              {/* Menus dropdown (andrew redmond only) */}
              {userInfo?.name === 'andrew redmond' && (
                <NavDropdown title='Menus' id='menus' style={dropdownMenuStyle}>
                  <LinkContainer to='/admin/productlist'>
                    <NavDropdown.Item style={dropdownItemStyle}>Menu Items</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/directoratelist'>
                    <NavDropdown.Item style={dropdownItemStyle}>Directorates</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/specialitylist'>
                    <NavDropdown.Item style={dropdownItemStyle}>Speciality</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to='/admin/activitylist'>
                    <NavDropdown.Item style={dropdownItemStyle}>Activities</NavDropdown.Item>
                  </LinkContainer>
                </NavDropdown>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* ── Mobile navbar (below lg) ── */}
      <Navbar
        style={{ backgroundColor: '#b1e1e7', height: '64px' }}
        className='d-flex d-lg-none px-3'
      >
        <LinkContainer to='/'>
          <Navbar.Brand>
            <img src={logo} alt='Beaumont' style={{ maxHeight: '48px', height: 'auto' }} />
          </Navbar.Brand>
        </LinkContainer>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', flex: 1, marginLeft: 8 }}>
          Shift Portal
        </span>
        <Button
          variant='link'
          onClick={() => setShowDrawer(true)}
          style={{ color: '#1a1a2e', fontSize: 24, padding: 4, position: 'relative' }}
          aria-label='Open menu'
        >
          <FaBars />
          {(pendingSwapCount > 0 || pendingApprovalCount > 0) && (
            <span
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 9,
                height: 9,
                borderRadius: '50%',
                backgroundColor: '#dc3545',
              }}
            />
          )}
        </Button>
      </Navbar>

      {/* ── Offcanvas drawer ── */}
      <Offcanvas
        show={showDrawer}
        onHide={closeDrawer}
        placement='end'
        style={{ width: 300, maxWidth: '85vw' }}
      >
        <div style={{
          backgroundColor: '#b1e1e7',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>
              {userInfo ? userInfo.name : 'Beaumont Portal'}
            </div>
            {userInfo && (
              <div style={{ fontSize: 11, color: '#555' }}>
                {userInfo.isAdmin   ? 'Administrator'
                  : userInfo.isManager  ? 'Manager'
                  : userInfo.isRadAdmin ? 'Radiation Admin'
                  : userInfo.isReporter ? 'Reporter'
                  : 'Staff'}
              </div>
            )}
          </div>
          <Button
            variant='link'
            onClick={closeDrawer}
            style={{ color: '#1a1a2e', fontSize: 20, padding: 0 }}
          >
            <FaTimes />
          </Button>
        </div>

        <Offcanvas.Body style={{ padding: 0, overflowY: 'auto' }}>

          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
            <SearchBox />
          </div>

          {!userInfo && (
            <DrawerItem to='/login' onClick={closeDrawer}>
              <FaUser style={{ marginRight: 8 }} /> Sign In
            </DrawerItem>
          )}

          {/* My Account  removed link  <DrawerItem to='/my-availability' onClick={closeDrawer}>My Availability</DrawerItem> */}
          {userInfo && (
            <>
              <SectionLabel>My Account</SectionLabel>
              <DrawerItem to='/profile'         onClick={closeDrawer}>Profile</DrawerItem>
              
              <DrawerItem to='/my-roster'       onClick={closeDrawer}>My Roster</DrawerItem>
              <DrawerItem to='/my-shifts/request-swap' onClick={closeDrawer}>

                <span>Shift Swaps</span>
                <CountBadge count={pendingSwapCount} />
              </DrawerItem>
              <DrawerItem to='/arcade' onClick={closeDrawer}>My Skills</DrawerItem>
               <DrawerItem onClick={() => { openGuide('staff'); closeDrawer(); }}>
               Staff Guide
               </DrawerItem>

            </>
          )}

          {/* Manager */}
          {userInfo?.isManager && (
            <>
              <SectionLabel>Manager</SectionLabel>
              <DrawerItem to='/manager/ward-preshifts'       onClick={closeDrawer}>Request Shifts</DrawerItem>
              <DrawerItem to='/manager/ward-weekly-schedule' onClick={closeDrawer}>Ward Weekly Schedule</DrawerItem>
              <DrawerItem to='/manager/approved-filled'      onClick={closeDrawer}>Worked Shifts (Pending)</DrawerItem>
              <DrawerItem to='/manager/completed-shifts'     onClick={closeDrawer}>Approved &amp; Completed</DrawerItem>
              <DrawerItem to='/manager/MonthlyCalendarView'  onClick={closeDrawer}>Monthly Calendar Stats</DrawerItem>
              <DrawerItem to='/manager/staffWeeklySchedule'  onClick={closeDrawer}>Staff Weekly Schedule</DrawerItem>
              <DrawerItem to='/manager/staff-availability'   onClick={closeDrawer}>Staff Availability</DrawerItem>
              <DrawerItem to='/manager/swap-approvals'       onClick={closeDrawer}>
                <span>Shift Swap Approvals</span>
                <CountBadge count={pendingApprovalCount} />
              </DrawerItem>
              <DrawerItem to='/manager/dashboard'            onClick={closeDrawer}>My Dashboard</DrawerItem>
               <DrawerItem onClick={() => { openGuide('manager'); closeDrawer(); }}>Manager Guide</DrawerItem>
               <DrawerItem onClick={() => { openGuide('early-finishes'); closeDrawer(); }}>Shift - Early Finishes</DrawerItem>

            </>
          )}

          {/* Admin */}
          {userInfo?.isAdmin && (
            <>
              <SectionLabel>Shift Assignments</SectionLabel>
              <DrawerItem to='/admin/shifts'               onClick={closeDrawer}>Create and Manage Live Shifts</DrawerItem>
              <DrawerItem to='/admin/staff-availability'   onClick={closeDrawer}>Staff Availability</DrawerItem>
              <DrawerItem to='/admin/ward-weekly-schedule' onClick={closeDrawer}>Ward Weekly Schedule</DrawerItem>
              <DrawerItem to='/admin/staffWeeklySchedule'  onClick={closeDrawer}>Staff Weekly Schedule</DrawerItem>
              <DrawerItem to='/admin/ward-preshifts'       onClick={closeDrawer}>Pre-Shift Requests</DrawerItem>
              <DrawerItem to='/admin/rolling-roster'       onClick={closeDrawer}>6-Week Rolling Roster</DrawerItem>

              <DrawerItem to='/manager/swap-approvals'     onClick={closeDrawer}>
                <span>Shift Swap Approvals</span>
                <CountBadge count={pendingApprovalCount} />
              </DrawerItem>

              <DrawerItem to='/admin/approved-filled'      onClick={closeDrawer}>Worked Shifts (Pending)</DrawerItem>
              <DrawerItem to='/admin/completed-shifts'     onClick={closeDrawer}>Approved &amp; Completed</DrawerItem>

              <SectionLabel>Dashboards</SectionLabel>
              <DrawerItem to='/admin/dashboard'            onClick={closeDrawer}>Dashboard</DrawerItem>
              <DrawerItem to='/admin/MonthlyCalendarView'  onClick={closeDrawer}>Monthly Calendar Stats</DrawerItem>

              <SectionLabel>Agency</SectionLabel>
              <DrawerItem to='/admin/agencies'             onClick={closeDrawer}>Manage Agencies</DrawerItem>
              <DrawerItem to='/admin/agency-tier-response-times' onClick={closeDrawer}>Agency Tier Response Times</DrawerItem>

              <SectionLabel>Configuration</SectionLabel>
              <DrawerItem to='/admin/userlist'             onClick={closeDrawer}>Users</DrawerItem>
              <DrawerItem to='/admin/hospital-locations'   onClick={closeDrawer}>Manage Hospital Locations</DrawerItem>
              {/* ── NEW: Availability Type Configuration ── */}
              <DrawerItem to='/admin/availability-config'  onClick={closeDrawer}>Availability Type Configuration</DrawerItem>
              <DrawerItem to='/admin/audit-log'            onClick={closeDrawer}>Shift Audit Log</DrawerItem>

                <SectionLabel>Annual Leave</SectionLabel>
              <DrawerItem to='/admin/annual-leave'         onClick={closeDrawer}>Annual Leave Calculator</DrawerItem>
              <DrawerItem to='/admin/non-bank-annual-leave' onClick={closeDrawer}>Non Bank Annual Leave</DrawerItem>
              <DrawerItem to='/admin/annual-leave-config' onClick={closeDrawer}>Annual Leave Configuration</DrawerItem>

               <SectionLabel>Guides</SectionLabel>
               <DrawerItem onClick={() => { openGuide('admin'); closeDrawer(); }}>Admin Guide</DrawerItem>
              <DrawerItem onClick={() => { openGuide('manager'); closeDrawer(); }}>Manager Guide</DrawerItem>


 
 <DrawerItem onClick={() => { openGuide('early-finishes'); closeDrawer(); }}>Shift - Early Finishes</DrawerItem>
 <DrawerItem onClick={() => { openGuide('agency-portal-guide'); closeDrawer(); }}>Agency portal guide</DrawerItem>


              <DrawerItem onClick={() => { openGuide('annual-leave'); closeDrawer(); }}>
                  Annual Leave Entitlement Guide
                </DrawerItem>

                <DrawerItem onClick={() => { openGuide('rolling-roster'); closeDrawer(); }}>
                  6-Week Rolling Roster Guide
                </DrawerItem>



            </>
          )}

          {userInfo?.isReporter && !userInfo?.isAdmin && (
            <>
              <SectionLabel>Reports</SectionLabel>
              <DrawerItem to='/admin/dashboard' onClick={closeDrawer}>Dashboard</DrawerItem>
              <DrawerItem to='/admin/completed-shifts' onClick={closeDrawer}>Approved &amp; Completed</DrawerItem>
               <DrawerItem to='/admin/agency-dashboard' onClick={closeDrawer}>Agency Dashboard</DrawerItem>
                <DrawerItem to='/admin/preshift-analytics' onClick={closeDrawer}>Pre Shift Requests Dashboard</DrawerItem>
                 <DrawerItem to='/admin/annual-leave' onClick={closeDrawer}>Annual Leave Calculator</DrawerItem>
                 <DrawerItem to='/admin/non-bank-annual-leave' onClick={closeDrawer}>Non Bank Annual Leave</DrawerItem>
                 <DrawerItem to='/admin/annual-leave-config' onClick={closeDrawer}>Annual Leave Configuration</DrawerItem>
                    </>


          )}


          {/* Rad Admin */}
          {userInfo?.isRadAdmin && (
            <>
              <SectionLabel>Radiation Admin</SectionLabel>
              <DrawerItem to='/admin/shiftApproval'                onClick={closeDrawer}>Shift Approval</DrawerItem>
              <DrawerItem to='/admin/shiftAgencyNotification'      onClick={closeDrawer}>Agency Notification</DrawerItem>
              <DrawerItem to='/admin/shiftRequestAgency'           onClick={closeDrawer}>Agency Request</DrawerItem>
              <DrawerItem to='/admin/shiftRequests'                onClick={closeDrawer}>Staff Shift Requests</DrawerItem>
              <DrawerItem to='/admin/RadAdmin/radiationsafetylist' onClick={closeDrawer}>Radiation Safety Records</DrawerItem>
              <DrawerItem to='/Admin/userlist/Rad'                 onClick={closeDrawer}>Users</DrawerItem>
            </>
          )}

          {/* Menus (andrew redmond only) */}
          {userInfo?.name === 'andrew redmond' && (
            <>
              <SectionLabel>Menus</SectionLabel>
              <DrawerItem to='/admin/productlist'     onClick={closeDrawer}>Menu Items</DrawerItem>
              <DrawerItem to='/admin/directoratelist' onClick={closeDrawer}>Directorates</DrawerItem>
              <DrawerItem to='/admin/specialitylist'  onClick={closeDrawer}>Speciality</DrawerItem>
              <DrawerItem to='/admin/activitylist'    onClick={closeDrawer}>Activities</DrawerItem>
            </>
          )}

          {/* Logout */}
          {userInfo && (
            <>
              <SectionLabel>Session</SectionLabel>
              <DrawerItem onClick={logoutHandler}>
                <span style={{ color: '#c0392b', fontWeight: 600 }}>Logout</span>
              </DrawerItem>
            </>
          )}

        </Offcanvas.Body>
      </Offcanvas>
    </header>
  );
};

export default Header;